import { env } from './env';

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * One shared worker file, one registration per studio scope. The worker
 * derives its cache names from its scope, so studios never share caches.
 */
export async function registerStudioWorker(slug: string): Promise<ServiceWorkerRegistration | undefined> {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return undefined;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: `/s/${slug}/` });
  } catch (err) {
    console.warn('[pwa] service worker registration failed', err);
    return undefined;
  }
}

export type PushState =
  | { kind: 'available' }
  | { kind: 'ios-install-required' }
  | { kind: 'denied' }
  | { kind: 'unsupported'; reason: string };

/** Honest capability check: never promise push to a plain iPhone tab. */
export function pushState(): PushState {
  if (!env.VITE_VAPID_PUBLIC_KEY) return { kind: 'unsupported', reason: 'Уведомления в этой студии пока не настроены.' };
  if (isIos() && !isStandalone()) return { kind: 'ios-install-required' };
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return { kind: 'unsupported', reason: 'Этот браузер не поддерживает уведомления.' };
  }
  if (Notification.permission === 'denied') return { kind: 'denied' };
  return { kind: 'available' };
}

function b64urlToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replaceAll('-', '+').replaceAll('_', '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function subscribeToPush(slug: string): Promise<{ endpoint: string; p256dh: string; auth: string }> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('permission_denied');
  const reg = (await navigator.serviceWorker.getRegistration(`/s/${slug}/`)) ?? (await registerStudioWorker(slug));
  if (!reg) throw new Error('no_service_worker');
  await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64urlToBytes(env.VITE_VAPID_PUBLIC_KEY) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}
